import React, { useEffect } from "react";
import { Alert, Spinner } from "react-bootstrap";
import { Link, useLocation, useNavigate } from "react-router-dom";
import RIcon from "./ReservasIcons";
import { SUCURSALES_COPY, backTarget, sucursalPath, sucursalesLabel } from "./sucursalesModel";
import "./sucursales.css";

export const SUCURSALES_ANCHOR = "sucursales";

const placeOf = (item) =>
  [item?.address, item?.city?.name || item?.cityid?.name || (typeof item?.city === "string" ? item.city : "")]
    .filter(Boolean)
    .join(", ");

/** Chip of a listing card; renders nothing without branches. */
export const SucursalesChip = ({ count }) => {
  const label = sucursalesLabel(count);
  if (!label) return null;
  return (
    <span className="sucursales-chip" data-testid="sucursales-chip">
      <RIcon name="pin" width={13} height={13} />
      {label}
    </span>
  );
};

/** Banner above the hero of a sucursal landing. */
export const SucursalBanner = ({ kind, casaCentral }) => {
  if (!casaCentral) return null;
  return (
    <div className="sucursal-banner" data-testid="sucursal-banner">
      <span className="sucursal-banner-pill">Sucursal</span>
      <span className="sucursal-banner-text">
        Esta es una sucursal de <strong>{casaCentral.name}</strong>.
      </span>
      <Link className="sucursal-banner-link" to={`${sucursalPath(kind, casaCentral)}#${SUCURSALES_ANCHOR}`}>
        Ver las otras sucursales
      </Link>
    </div>
  );
};

/**
 * "Sucursales" section of a casa central landing: one card per sucursal with a
 * link to its own landing. `id` is the DOM id the rubro's tabs scroll to.
 */
export const SucursalesSection = (props) =>
  Array.isArray(props.sucursales) && props.sucursales.length > 0 ? <SucursalesSectionBody {...props} /> : null;

const SucursalesSectionBody = ({ kind, sucursales, id = SUCURSALES_ANCHOR, isDesktop = false, sedes = null }) => {
  const { hash } = useLocation();
  useEffect(() => {
    if (hash !== `#${SUCURSALES_ANCHOR}`) return;
    const el = document.getElementById(id);
    if (el && typeof el.scrollIntoView === "function") el.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [hash, id]);

  return (
    <div id={id} className={isDesktop ? "bd-section-stacked" : "rmob-section"} data-testid="sucursales-section">
      <h3 className={isDesktop ? "section-title" : "rmob-section-title"}>Sucursales</h3>
      <p className="sucursales-intro">{SUCURSALES_COPY[kind]}</p>
      <div className="sucursales-grid">
        {/* In-place mode (beauty): the casa central is sede #1 and every card can be picked. */}
        {(sedes ? [sedes.casa, ...sucursales] : sucursales).map((item, index) => {
          const place = placeOf(item);
          const active = sedes && String(item._id) === String(sedes.activeId);
          return (
            <div className={`sucursal-card${active ? " is-selected" : ""}`} key={String(item._id)} data-testid="sucursal-card">
              <div className="sucursal-card-name">{item.name}</div>
              {place && (
                <div className="sucursal-card-place">
                  <RIcon name="pin" width={14} height={14} />
                  {place}
                </div>
              )}
              {sedes && (
                <button
                  type="button"
                  className="r-btn-soft sucursal-card-pick"
                  disabled={active}
                  onClick={() => sedes.onSelect(item)}
                >
                  {active ? "Elegida" : "Reservar acá"}
                </button>
              )}
              {!(sedes && index === 0) && (
                <Link className="sucursal-card-link" to={sucursalPath(kind, item)}>
                  Ver sucursal
                </Link>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

/**
 * "¿En qué sucursal?" list of the booking panel of a casa central. The casa
 * central (current) comes first and selected; choosing another one navigates to
 * its landing, which owns its own catalogue. With `sedes` (beauty) it selects in place.
 */
export const SucursalPicker = (props) =>
  Array.isArray(props.sucursales) && props.sucursales.length > 0 ? <SucursalPickerBody {...props} /> : null;

const SucursalPickerBody = ({ kind, currentName, sucursales, sedes = null }) => {
  const navigate = useNavigate();
  // In-place mode: every sede, the casa central first, switches the page without navigating.
  const all = sedes ? [sedes.casa, ...sucursales] : null;
  if (all) {
    return (
      <div className="sucursal-picker" role="radiogroup" aria-label="¿En qué sucursal?" data-testid="sucursal-picker">
        <div className="sucursal-picker-title">¿En qué sucursal?</div>
        {all.map((item) => {
          const active = String(item._id) === String(sedes.activeId);
          return (
            <button
              key={String(item._id)}
              type="button"
              role="radio"
              aria-checked={active ? "true" : "false"}
              className={`sucursal-option${active ? " is-selected" : ""}`}
              onClick={() => sedes.onSelect(item)}
            >
              <span className="sucursal-radio" aria-hidden="true" />
              <span className="sucursal-option-name">{item.name}</span>
            </button>
          );
        })}
      </div>
    );
  }
  return (
    <div className="sucursal-picker" role="radiogroup" aria-label="¿En qué sucursal?" data-testid="sucursal-picker">
      <div className="sucursal-picker-title">¿En qué sucursal?</div>
      <button type="button" role="radio" aria-checked="true" className="sucursal-option is-selected">
        <span className="sucursal-radio" aria-hidden="true" />
        <span className="sucursal-option-name">{currentName}</span>
      </button>
      {sucursales.map((item) => (
        <button
          key={String(item._id)}
          type="button"
          role="radio"
          aria-checked="false"
          className="sucursal-option"
          onClick={() => navigate(sucursalPath(kind, item))}
        >
          <span className="sucursal-radio" aria-hidden="true" />
          <span className="sucursal-option-name">{item.name}</span>
        </button>
      ))}
    </div>
  );
};

/** Compact "Sucursal: X · Cambiar" row of the booking panel of a sucursal. */
export const SucursalCurrent = ({ kind, name, casaCentral }) => {
  if (!casaCentral) return null;
  return (
    <div className="sucursal-current" data-testid="sucursal-current">
      <span>
        Sucursal: <strong>{name}</strong> ·
      </span>
      <Link to={`${sucursalPath(kind, casaCentral)}#${SUCURSALES_ANCHOR}`}>Cambiar</Link>
    </div>
  );
};

/** The swapped area while the chosen sede's detail loads, or failed (with retry). */
export const SedeSwap = ({ status }) => (
  <div className="sede-swap" data-testid="sede-swap">
    {status.status === "error" ? (
      <>
        <Alert variant="danger">{status.message || `No pudimos cargar ${status.name}.`}</Alert>
        <button type="button" className="r-btn-primary" onClick={status.onRetry}>
          Reintentar
        </button>
      </>
    ) : (
      <div role="status">
        <Spinner animation="border" />
        <p>Cargando {status.name}…</p>
      </div>
    )}
  </div>
);

/**
 * Casa central landing while its chosen sede loads or failed (clubs and bars): the
 * casa central's hero and the Sucursales section stay, the bookable area shows the status.
 */
export const SedePending = ({ kind, hero, heroImage, fallbackImage, sucursales, sedes, status, isMobile = false, panelTitle }) => {
  const navigate = useNavigate();
  const back = backTarget(kind, null);
  const img = heroImage || fallbackImage;
  const onImgError = (e) => { e.currentTarget.src = fallbackImage; };
  const section = <SucursalesSection kind={kind} sucursales={sucursales} id="sec-sucursales" isDesktop={!isMobile} sedes={sedes} />;
  if (isMobile) {
    return (
      <div className="reservas-root rmob-root">
        <div className="rmob-landing-hero">
          <img src={img} alt={hero.name} onError={onImgError} />
          <div className="rmob-hero-scrim" />
          <button className="rmob-back rmob-back-hero" onClick={() => navigate(back.path)} aria-label="Volver">
            <RIcon name="chevL" width={20} height={20} />
          </button>
          <div className="rmob-hero-info">
            <h1 className="rmob-hero-name">{hero.name}</h1>
          </div>
        </div>
        <div className="rmob-landing-body">
          {section}
          <SedeSwap status={status} />
        </div>
      </div>
    );
  }
  return (
    <div className="reservas-root">
      <div className="wrap bar-detail">
        <button className="crumb" onClick={() => navigate(back.path)}>
          <RIcon name="chevL" width={16} height={16} /> {back.label}
        </button>
        <div className="bd-hero">
          <img src={img} alt={hero.name} onError={onImgError} />
          <div className="bd-hero-scrim" />
          <div className="bd-hero-info">
            <h1>{hero.name}</h1>
            <div className="meta">
              {hero.address && <span><RIcon name="pin" width={16} height={16} /> {hero.address}</span>}
            </div>
          </div>
        </div>
        <div className="bd-grid bd-info-grid">
          <div>
            {section}
            <SedeSwap status={status} />
          </div>
          <div className="bd-sidebar-sticky">
            <div className="reserve-stick">
              <div className="rs-head"><div className="t">{panelTitle}</div><div className="n">{status.name}</div></div>
              <div className="rs-body">
                <SucursalPicker kind={kind} currentName={hero.name} sucursales={sucursales} sedes={sedes} />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
